var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import isEqual from "@tinkoff/utils/is/equal";
import favoriteSeriesTransformer from "./transformers/favorite-data-series-transformer";
import applyDropdownFilters from "./apply-dropdown-filters";
import { withResolvers } from "./promise";
import RequestManager from "./request-manager";
import QueuedMessageError from "./errors/queued-message-error";
export var RequestStatus;
(function (RequestStatus) {
    RequestStatus["Queued"] = "FavoriteDataFetcher::queued";
    RequestStatus["InFlight"] = "FavoriteDataFetcher::in-flight";
    RequestStatus["Complete"] = "FavoriteDataFetcher::complete";
})(RequestStatus || (RequestStatus = {}));
const DEBUG_ENABLED = new URLSearchParams(window.location.search).has("SPEEDCURVE_DEBUG");
class FavoriteDataFetcher {
    static setConcurrency(concurrency) {
        this.concurrency = concurrency;
    }
    static getConcurrency() {
        return this.concurrency || window.SPEEDCURVE_CONFIG.favoritesFetchConcurrency || this.DEFAULT_CONCURRENCY;
    }
    /**
     * Make a favorite-data request. Requests managed by the fetcher are deduplicated and made with
     * limited concurrency. Optionally provide a signal that can receive updates on the request
     * status.
     */
    static fetchData(request, signal) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestId = this.getRequestId(request);
            this.log("fetchData", requestId, request);
            if (this.requests.has(requestId)) {
                const existingRequest = this.requests.get(requestId);
                if (existingRequest && isEqual(existingRequest.request, request)) {
                    this.log("Reusing existing request", requestId);
                    return existingRequest.promise;
                }
            }
            const { promise, resolve, reject } = withResolvers();
            // Only one request can be made by a chart at any time. When any chart request is made,
            // the fetcher checks for existing requests for that chart and aborts them.
            if (this.requests.has(requestId)) {
                this.abortInFlightRequests(requestId);
            }
            if (signal) {
                signal(RequestStatus.Queued);
            }
            this.requests.set(requestId, { request, promise, resolve, reject, signal });
            this.requestQueue.add(requestId);
            this.processQueue();
            return promise;
        });
    }
    static _fetchData(request, queueMessageId) {
        const requestId = this.getRequestId(request);
        let url = this.generateDataUrl(request);
        if (queueMessageId) {
            url += `&queueMessageId=${queueMessageId}`;
        }
        // After existing requests have been aborted, an AbortController is made for the new request.
        const controller = new AbortController();
        this.controllers.set(requestId, controller);
        this.log("Making request", requestId, url);
        const queuedRequest = this.requests.get(requestId);
        queuedRequest.inFlight = true;
        if (queuedRequest.signal) {
            queuedRequest.signal(RequestStatus.InFlight);
        }
        return RequestManager.makeAccountRequest(url, { signal: controller.signal }, this.onResponse(request));
    }
    static processQueue() {
        if (this.requestQueue.size === 0) {
            // Nothing in the request queue
            return;
        }
        const inFlightRequests = this.getInFlightRequests();
        if (inFlightRequests.length >= this.getConcurrency()) {
            // Maximum concurrency reached
            return;
        }
        const nextRequestId = this.requestQueue.values().next().value;
        const nextRequest = this.requests.get(nextRequestId);
        if (!nextRequest) {
            return;
        }
        const { request, resolve, reject } = nextRequest;
        this.requestQueue.delete(nextRequestId);
        this._fetchData(request).then(resolve).catch(reject);
    }
    static getInFlightRequests() {
        return [...this.requests.values()].filter((req) => req.inFlight);
    }
    static onResponse(request) {
        const requestId = this.getRequestId(request);
        const finishRequest = () => {
            const queuedRequest = this.requests.get(requestId);
            if (queuedRequest === null || queuedRequest === void 0 ? void 0 : queuedRequest.signal) {
                queuedRequest.signal(RequestStatus.Complete);
            }
            this.requests.delete(requestId);
            this.controllers.delete(requestId);
            this.timers.delete(requestId);
            this.queueMessageIds.delete(requestId);
            this.processQueue();
        };
        return (res) => __awaiter(this, void 0, void 0, function* () {
            const { charts, queueMessageId, queueMessageStatus } = res;
            if (queueMessageStatus === "error") {
                finishRequest();
                throw new QueuedMessageError("Queued query returned an error", res);
            }
            else if (charts && charts.length) {
                // We received a full response. Finish the request and return the transformed response.
                const transformedResponse = favoriteSeriesTransformer(res);
                finishRequest();
                return transformedResponse;
            }
            else if (queueMessageId && queueMessageStatus !== "cancelled") {
                // The request is queued. Retry after the delay specified by the server.
                const delay = res.retryDelay || this.DEFAULT_RETRY_DELAY;
                // Store the queue message ID
                this.queueMessageIds.set(requestId, queueMessageId);
                const originalRequest = this.requests.get(requestId);
                if (originalRequest === null || originalRequest === void 0 ? void 0 : originalRequest.signal) {
                    originalRequest.signal(queueMessageStatus);
                }
                return yield this.runAfterDelay(requestId, delay, () => this._fetchData(request, queueMessageId));
            }
            // The request was cancelled or errored. Finish the request and return an empty array.
            finishRequest();
            return [];
        });
    }
    /**
     * Prepend the global query string (from the dropdown) when it is available
     */
    static prependGlobalQueryString(queryString) {
        if (typeof window.getDataURL !== "undefined") {
            return `${window.getDataURL()}&${queryString}`;
        }
        return `?${queryString}`;
    }
    static getRequestId(request) {
        return String(request.chart_id);
    }
    /**
     * Apply dropdown parameters and global options to a FavoriteDataRequest to generate the
     * favorite-data endpoint URL.
     */
    static generateDataUrl(request) {
        var _a;
        const globalConfig = window.SPEEDCURVE_CONFIG || {};
        const settingsWithDropdownFilters = applyDropdownFilters(request);
        const encodedSettings = encodeURIComponent(JSON.stringify([settingsWithDropdownFilters]));
        let url = `/favorite-data/${this.prependGlobalQueryString(`o=${encodedSettings}`)}`;
        if (typeof globalConfig.persistentUserNavState !== "undefined" && !globalConfig.persistentUserNavState) {
            url += "&disable-user-state-persist=1";
        }
        else if ((_a = globalConfig.disablePersistentState) === null || _a === void 0 ? void 0 : _a.length) {
            url += `&disable-persistent-state=${JSON.stringify(window.SPEEDCURVE_CONFIG.disablePersistentState)}`;
        }
        return url;
    }
    /**
     * Cancel any in-flight requests and stop any timers associated with a chart.
     */
    static abortInFlightRequests(requestId) {
        this.log("abortInFlightRequests", requestId);
        const timerId = this.timers.get(requestId);
        const controller = this.controllers.get(requestId);
        const queueMessageId = this.queueMessageIds.get(requestId);
        this.requestQueue.delete(requestId);
        if (queueMessageId) {
            this.cancelQueueMessage(queueMessageId);
            this.queueMessageIds.delete(requestId);
        }
        if (timerId) {
            clearTimeout(timerId);
            this.timers.delete(requestId);
        }
        if (controller) {
            controller.abort();
            this.controllers.delete(requestId);
        }
    }
    static cancelQueueMessage(queueMessageId) {
        RequestManager.makeAccountRequest(`/cancel-queue-message/?queueMessageId=${queueMessageId}`);
    }
    /**
     * Create a timer to run a function after a delay. If a chart ID is specified, the timer will
     * be stored so that it can be cancelled later.
     */
    static runAfterDelay(requestId, delay, fn) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                const timeoutId = window.setTimeout(() => {
                    resolve(fn());
                }, delay);
                this.timers.set(requestId, timeoutId);
            });
        });
    }
    static log(...args) {
        if (DEBUG_ENABLED) {
            // eslint-disable-next-line no-console
            console.log("[FavoriteDataFetcher]", ...args);
        }
    }
}
FavoriteDataFetcher.DEFAULT_RETRY_DELAY = 10000;
FavoriteDataFetcher.DEFAULT_CONCURRENCY = 2;
/** Map of timers for retrying requests that are waiting in the RUM query queue */
FavoriteDataFetcher.timers = new Map();
/** Map of AbortControllers for in-flight requests */
FavoriteDataFetcher.controllers = new Map();
/** Requests that are in progress, either queued or in-flight */
FavoriteDataFetcher.requests = new Map();
/** Requests waiting to be made */
FavoriteDataFetcher.requestQueue = new Set();
/** RUM query queue message IDs */
FavoriteDataFetcher.queueMessageIds = new Map();
export default FavoriteDataFetcher;
